import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import hrChatService from '../services/hrChatService';
import ROLE_SUGGESTIONS from '../config/roleSuggestions';
import { getTemplateSectionsForRole, getSmartSuggestions, findTemplateById } from '../utils/aiChatUx';

// ── Shared localStorage keys (dùng chung floating + full page) ──
export const AI_LS_CONV_LIST = 'ai_conv_list';
export const AI_LS_ACTIVE_ID = 'ai_conv_active_id';
export const aiLsConvKey = (id) => `ai_conv_${id}`;

// ── Helpers ──
export const genConvId = () => `conv_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
const genBranchId = () => `branch_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const cloneImages = (images) => {
  if (!Array.isArray(images) || images.length === 0) return undefined;
  const normalized = images
    .filter(img => img?.base64 && img?.mimeType)
    .map(img => ({
      base64: img.base64,
      mimeType: img.mimeType,
      name: img.name,
    }));

  return normalized.length > 0 ? normalized : undefined;
};

const cloneEditVersions = (versions) => {
  if (!Array.isArray(versions) || versions.length === 0) return undefined;
  return versions.map(version => ({
    content: version?.content || '',
    images: cloneImages(version?.images),
    branchId: version?.branchId || genBranchId(),
  }));
};

const RAW_MODE_HINT_REGEX = /(raw\s*mode|realtime|real\s*time|dữ\s*liệu\s*thô|du\s*lieu\s*tho|a\s*(đến|den|to|-)\s*z|from\s*a\s*to\s*z)/i;
const isRawModeMessage = (text = '') => RAW_MODE_HINT_REGEX.test(String(text || '').trim());

const getUserMessageVersions = (message) => {
  if (!message || message.role !== 'user') return [];

  if (Array.isArray(message.editVersions) && message.editVersions.length > 0) {
    return cloneEditVersions(message.editVersions) || [];
  }

  const fallbackBranchId = message.branchId || genBranchId();
  return [{
    content: message.content || '',
    images: cloneImages(message.images),
    branchId: fallbackBranchId,
  }];
};

const getActiveEditVersionIndex = (message, versions = getUserMessageVersions(message)) => {
  if (!versions.length) return 0;

  const rawIndex = message?.editVersionIndex;
  if (Number.isInteger(rawIndex) && rawIndex >= 0 && rawIndex < versions.length) {
    return rawIndex;
  }

  const branchMatchIndex = versions.findIndex(version => version.branchId === message?.branchId);
  return branchMatchIndex >= 0 ? branchMatchIndex : 0;
};

const hydrateMessage = (message) => {
  if (!message) return message;

  const hydrated = {
    ...message,
    timestamp: message.timestamp ? new Date(message.timestamp) : new Date(),
  };

  if (hydrated.role === 'user') {
    const versions = getUserMessageVersions(hydrated);
    const activeIndex = getActiveEditVersionIndex(hydrated, versions);
    const activeVersion = versions[activeIndex] || versions[0];

    return {
      ...hydrated,
      content: activeVersion?.content ?? hydrated.content ?? '',
      images: cloneImages(activeVersion?.images) ?? cloneImages(hydrated.images),
      branchId: activeVersion?.branchId || hydrated.branchId || genBranchId(),
      editVersions: versions,
      editVersionIndex: activeIndex,
    };
  }

  return {
    ...hydrated,
    images: cloneImages(hydrated.images),
  };
};

const hydrateMessages = (messages = []) => messages.map(hydrateMessage);

const cloneSnapshotEntry = (entry) => ({
  messages: hydrateMessages(entry?.messages || []),
  dataSnapshot: entry?.dataSnapshot || null,
});

const serializeBranchSnapshots = (branchSnapshots) => {
  if (!(branchSnapshots instanceof Map)) return [];

  return Array.from(branchSnapshots.entries()).map(([branchId, entry]) => ([
    branchId,
    {
      messages: entry?.messages || [],
      dataSnapshot: entry?.dataSnapshot || null,
    }
  ]));
};

const hydrateBranchSnapshots = (entries = []) => {
  if (!Array.isArray(entries)) return new Map();

  return new Map(entries.map(([branchId, entry]) => ([
    branchId,
    cloneSnapshotEntry(entry),
  ])));
};

export const loadConvList = () => {
  try { return JSON.parse(localStorage.getItem(AI_LS_CONV_LIST)) || []; } catch { return []; }
};

export const loadConvData = (id) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(aiLsConvKey(id)));
    if (!parsed) return null;

    return {
      ...parsed,
      messages: hydrateMessages(parsed.messages || []),
      branchSnapshots: hydrateBranchSnapshots(parsed.branchSnapshots || []),
      dataSnapshot: parsed.dataSnapshot || null,
    };
  } catch {
    return null;
  }
};

export const saveConvData = (id, messages, snapshot, branchSnapshots = new Map()) => {
  try {
    localStorage.setItem(aiLsConvKey(id), JSON.stringify({
      messages,
      dataSnapshot: snapshot,
      branchSnapshots: serializeBranchSnapshots(branchSnapshots),
    }));
  } catch {}
};

export const formatRelTime = (isoStr) => {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 1) return 'Vừa xong';
  if (m < 60) return `${m} phút trước`;
  if (h < 24) return `${h} giờ trước`;
  if (d < 7) return `${d} ngày trước`;
  return new Date(isoStr).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

// ── Markdown renderer (shared) ──
export const parseMarkdown = (text) => {
  if (!text) return '';
  if (typeof text !== 'string') text = String(text);
  return text
    .replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{1F300}-\u{1F9FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]/gu, '')
    .replace(/\[KHẨN CẤP\]/g, '<span class="ai-tag ai-tag--urgent">KHẨN CẤP</span>')
    .replace(/\[CẢNH BÁO\]/g, '<span class="ai-tag ai-tag--warn">CẢNH BÁO</span>')
    .replace(/\[BÌNH THƯỜNG\]/g, '<span class="ai-tag ai-tag--ok">BÌNH THƯỜNG</span>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^### (.+)$/gm, '<div class="ai-heading">$1</div>')
    .replace(/^## (.+)$/gm, '<div class="ai-heading ai-heading--lg">$1</div>')
    .replace(/^# (.+)$/gm, '<div class="ai-heading ai-heading--xl">$1</div>')
    .replace(/^(\d+)\.\s+(.+)$/gm, '<div class="ai-li ai-li--num"><span class="ai-li-num">$1.</span><span>$2</span></div>')
    .replace(/^[-•]\s+(.+)$/gm, '<div class="ai-li"><span class="ai-li-dot"></span><span>$1</span></div>')
    .replace(/\n{2,}/g, '<div class="ai-para-br"></div>')
    .replace(/\n/g, '<br/>');
};

// ── Context ──
const AIChatContext = createContext(null);

export const AIChatProvider = ({ children }) => {
  const { user } = useAuth();
  const role = user?.role || 'EMPLOYEE';
  const roleConfig = ROLE_SUGGESTIONS[role] || ROLE_SUGGESTIONS.EMPLOYEE;

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [convList, setConvList] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [dataSnapshot, setDataSnapshot] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [branchSnapshots, setBranchSnapshots] = useState(new Map());
  const personalBriefRequestMapRef = useRef(new Set());
  const activeChatAbortRef = useRef(null);

  const templateSections = useMemo(() => getTemplateSectionsForRole(role), [role]);
  const smartSuggestions = useMemo(() => getSmartSuggestions({
    role,
    dataSnapshot,
    messages
  }), [role, dataSnapshot, messages]);

  const buildWelcome = useCallback(() => ({
    role: 'assistant',
    content: `Xin chào **${user?.fullName || 'bạn'}**!\n\nTôi là trợ lý AI — **${roleConfig.label}**.\n\nTôi có thể giúp bạn:\n- Trả lời câu hỏi về hệ thống (nhân sự, lương, hợp đồng, xe, tài sản...)\n- Trả lời **bất kỳ câu hỏi nào** như một AI thông thường (toán, code, viết lách, kiến thức chung...)\n\nHỏi tôi bất cứ điều gì!`,
    timestamp: new Date(),
  }), [user, roleConfig]);

  const syncSnapshotsForConversation = useCallback((conversationMessages, snapshot = null) => {
    const hydratedConversation = hydrateMessages(conversationMessages);

    setBranchSnapshots(prev => {
      const next = new Map(prev);

      hydratedConversation.forEach(message => {
        if (message.role !== 'user') return;

        const activeVersion = message.editVersions?.[message.editVersionIndex ?? 0];
        const activeBranchId = activeVersion?.branchId || message.branchId;
        if (!activeBranchId) return;

        next.set(activeBranchId, {
          messages: hydratedConversation,
          dataSnapshot: snapshot,
        });
      });

      return next;
    });
  }, []);

  const patchSnapshotsForVersions = useCallback((messageIndex, versions) => {
    if (!Array.isArray(versions) || versions.length === 0) return;

    const normalizedVersions = cloneEditVersions(versions) || [];
    const versionBranchIds = new Set(normalizedVersions.map(version => version.branchId));

    setBranchSnapshots(prev => {
      const next = new Map(prev);
      let hasChanged = false;

      next.forEach((entry, snapshotId) => {
        const snapshotMessage = entry?.messages?.[messageIndex];
        if (!snapshotMessage || snapshotMessage.role !== 'user') return;

        const snapshotVersions = getUserMessageVersions(snapshotMessage);
        const overlaps = snapshotVersions.some(version => versionBranchIds.has(version.branchId));
        if (!overlaps) return;

        const snapshotActiveIndex = getActiveEditVersionIndex(snapshotMessage, snapshotVersions);
        const snapshotActiveBranchId = snapshotVersions[snapshotActiveIndex]?.branchId || snapshotMessage.branchId;
        const resolvedIndex = Math.max(0, normalizedVersions.findIndex(version => version.branchId === snapshotActiveBranchId));
        const resolvedVersion = normalizedVersions[resolvedIndex] || normalizedVersions[0];

        const patchedMessages = hydrateMessages(entry.messages || []);
        patchedMessages[messageIndex] = hydrateMessage({
          ...patchedMessages[messageIndex],
          content: resolvedVersion.content,
          images: cloneImages(resolvedVersion.images),
          branchId: resolvedVersion.branchId,
          editVersions: normalizedVersions,
          editVersionIndex: resolvedIndex,
        });

        next.set(snapshotId, {
          ...entry,
          messages: patchedMessages,
        });
        hasChanged = true;
      });

      return hasChanged ? next : prev;
    });
  }, []);

  // ── Init: load active conv from localStorage ──
  useEffect(() => {
    if (!user) return;
    const list = loadConvList();
    setConvList(list);

    const activeId = localStorage.getItem(AI_LS_ACTIVE_ID);
    if (activeId) {
      const data = loadConvData(activeId);
      if (data?.messages?.length > 0) {
        setMessages(data.messages);
        setDataSnapshot(data.dataSnapshot || null);
        setBranchSnapshots(data.branchSnapshots || new Map());
        setActiveConvId(activeId);
        setShowSuggestions(false);
        return;
      }
    }

    const newId = genConvId();
    const welcome = buildWelcome();
    setMessages([welcome]);
    setDataSnapshot(null);
    setBranchSnapshots(new Map());
    setActiveConvId(newId);
    setShowSuggestions(true);
    localStorage.setItem(AI_LS_ACTIVE_ID, newId);
    saveConvData(newId, [welcome], null, new Map());
  }, [user, buildWelcome]);

  // ── Auto personal assistant brief cho EMPLOYEE (mỗi ngày, mỗi conversation) ──
  useEffect(() => {
    if (!user || role !== 'EMPLOYEE') return;
    if (!activeConvId || messages.length === 0) return;

    const dateKey = new Date().toISOString().slice(0, 10);
    const hasBriefInConversation = messages.some((message) => (
      message.role === 'assistant'
      && message.meta?.type === 'personal-brief'
      && message.meta?.dateKey === dateKey
    ));

    if (hasBriefInConversation) return;

    const requestKey = `${activeConvId}:${dateKey}`;
    if (personalBriefRequestMapRef.current.has(requestKey)) return;
    personalBriefRequestMapRef.current.add(requestKey);

    let cancelled = false;

    (async () => {
      try {
        const result = await hrChatService.getPersonalBrief();
        if (cancelled || !result?.message) return;

        const briefMessage = hydrateMessage({
          role: 'assistant',
          content: result.message,
          timestamp: new Date(),
          dataSnapshot: result.dataSnapshot || null,
          meta: {
            type: 'personal-brief',
            dateKey,
            generatedAt: result.generatedAt || new Date().toISOString()
          }
        });

        setMessages((prev) => {
          const hydratedPrev = hydrateMessages(prev);
          const exists = hydratedPrev.some((message) => (
            message.role === 'assistant'
            && message.meta?.type === 'personal-brief'
            && message.meta?.dateKey === dateKey
          ));
          if (exists) return hydratedPrev;
          return [...hydratedPrev, briefMessage];
        });

        if (result.dataSnapshot) {
          setDataSnapshot(result.dataSnapshot);
        }
      } catch (error) {
        personalBriefRequestMapRef.current.delete(requestKey);
        // Không chặn luồng chat nếu brief lỗi
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, role, activeConvId, messages]);

  // ── Auto-save khi messages / branch thay đổi ──
  useEffect(() => {
    if (!activeConvId || messages.length === 0) return;

    saveConvData(activeConvId, messages.slice(-100), dataSnapshot, branchSnapshots);

    const userMsgs = messages.filter(m => m.role === 'user' && typeof m.content === 'string' && m.content.trim());
    if (userMsgs.length === 0) return;

    const firstUserContent = userMsgs[0].content || '';
    const title = firstUserContent.substring(0, 50) + (firstUserContent.length > 50 ? '…' : '');
    const lastContent = typeof messages[messages.length - 1]?.content === 'string' ? messages[messages.length - 1].content : '';
    const preview = lastContent.substring(0, 70) + (lastContent.length > 70 ? '…' : '');

    setConvList(prev => {
      const list = [...prev];
      const idx = list.findIndex(c => c.id === activeConvId);
      const meta = {
        id: activeConvId,
        title,
        preview,
        createdAt: list[idx]?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        msgCount: messages.length,
      };

      if (idx >= 0) list[idx] = meta;
      else list.unshift(meta);

      const trimmed = list.slice(0, 50);
      try { localStorage.setItem(AI_LS_CONV_LIST, JSON.stringify(trimmed)); } catch {}
      return trimmed;
    });
  }, [activeConvId, messages, dataSnapshot, branchSnapshots]);

  const isCancelledRequest = useCallback((error) => (
    error?.code === 'ERR_CANCELED'
    || error?.name === 'CanceledError'
    || /canceled|aborted/i.test(String(error?.message || ''))
  ), []);

  const stopGeneration = useCallback(() => {
    if (!activeChatAbortRef.current) return;
    activeChatAbortRef.current.abort('USER_STOPPED');
    activeChatAbortRef.current = null;
  }, []);

  // ── Send message (with optional images) ──
  const sendMessage = useCallback(async (text, images = [], requestOptions = {}) => {
    const msgText = (typeof text === 'string' ? text : '').trim();
    const transportText = (typeof requestOptions?.messageText === 'string'
      ? requestOptions.messageText
      : msgText).trim();

    if ((!msgText && images.length === 0) || (!transportText && images.length === 0) || isLoading) return;

    const normalizedImages = cloneImages(images);
    const branchId = genBranchId();
    const userMsg = hydrateMessage({
      role: 'user',
      content: msgText,
      timestamp: new Date(),
      images: normalizedImages,
      branchId,
      editVersions: [{
        content: msgText,
        images: normalizedImages,
        branchId,
      }],
      editVersionIndex: 0,
    });

    const baseMessages = hydrateMessages([...messages, userMsg]);
    setMessages(baseMessages);
    setShowSuggestions(false);
    setIsLoading(true);

    const requestAbortController = new AbortController();
    activeChatAbortRef.current = requestAbortController;

    try {
      const history = messages
        .filter(message => message.role !== 'system')
        .map(message => ({ role: message.role, content: message.content }));

      const result = await hrChatService.sendMessage(transportText, history, images, {
        signal: requestAbortController.signal,
        rawMode: requestOptions?.rawMode === true || isRawModeMessage(transportText)
      });

      // Parse JSON response from AI (format: {"action":"final","answer":"...markdown..."})
      let aiContent = result.response || '';
      try {
        const parsed = JSON.parse(aiContent);
        if (parsed.answer) {
          aiContent = parsed.answer;
        } else if (parsed.content) {
          aiContent = parsed.content;
        }
      } catch {
        // Fallback khi AI trả JSON bị cắt cụt
        const rawAiContent = String(aiContent || '').trim();
        const likelyJsonPayload = rawAiContent.startsWith('{')
          || rawAiContent.startsWith('```json')
          || /"action"\s*:|"answer"\s*:/i.test(rawAiContent);

        if (likelyJsonPayload) {
          const answerMatch = rawAiContent.match(/"answer"\s*:\s*"([\s\S]*)$/i);
          if (answerMatch && answerMatch[1]) {
            aiContent = answerMatch[1]
              .replace(/"\s*\}?\s*$/, '')
              .replace(/\\n/g, '\n')
              .replace(/\\r/g, '')
              .replace(/\\t/g, '\t')
              .replace(/\\"/g, '"')
              .trim();
          }
        }
      }

      aiContent = String(aiContent || '').replace(/\\n/g, '\n');

      const assistantMsg = hydrateMessage({
        role: 'assistant',
        content: aiContent,
        timestamp: new Date(),
        dataSnapshot: result.dataSnapshot,
        meta: result.meta,
        branchId,
      });

      const finalMessages = hydrateMessages([...baseMessages, assistantMsg]);
      setMessages(finalMessages);
      if (result.dataSnapshot) setDataSnapshot(result.dataSnapshot);
      syncSnapshotsForConversation(finalMessages, result.dataSnapshot || null);
    } catch (err) {
      if (isCancelledRequest(err)) {
        const stoppedAssistantMsg = hydrateMessage({
          role: 'assistant',
          content: 'Đã dừng phản hồi AI theo yêu cầu.',
          timestamp: new Date(),
          meta: { stopped: true },
          branchId,
        });

        const finalMessages = hydrateMessages([...baseMessages, stoppedAssistantMsg]);
        setMessages(finalMessages);
        syncSnapshotsForConversation(finalMessages, dataSnapshot || null);
      } else {
        const errMsg = err.response?.data?.error || err.message || 'Có lỗi xảy ra';
        const errorAssistantMsg = hydrateMessage({
          role: 'assistant',
          content: errMsg,
          timestamp: new Date(),
          isError: true,
          branchId,
        });

        const finalMessages = hydrateMessages([...baseMessages, errorAssistantMsg]);
        setMessages(finalMessages);
        syncSnapshotsForConversation(finalMessages, dataSnapshot || null);
      }
    } finally {
      if (activeChatAbortRef.current === requestAbortController) {
        activeChatAbortRef.current = null;
      }
      setIsLoading(false);
    }
  }, [messages, isLoading, dataSnapshot, syncSnapshotsForConversation, isCancelledRequest]);

  const sendTemplateMessage = useCallback((templateId, payload = {}) => {
    const template = findTemplateById(templateId);
    if (!template) return;

    const variables = {
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      ...payload
    };

    const compiledPrompt = Object.entries(variables).reduce((result, [key, value]) => (
      result.replaceAll(`{{${key}}}`, String(value))
    ), template.prompt);

    setShowSuggestions(false);
    sendMessage(compiledPrompt);
  }, [sendMessage]);

  const editMessage = useCallback(async (msgIndex, newText, newImages = [], requestOptions = {}) => {
    if (isLoading) return;

    const trimmedText = (typeof newText === 'string' ? newText : '').trim();
    const transportText = (typeof requestOptions?.messageText === 'string'
      ? requestOptions.messageText
      : trimmedText).trim();
    const targetMsg = messages[msgIndex];
    if (!targetMsg || targetMsg.role !== 'user' || !trimmedText || !transportText) return;

    const existingVersions = getUserMessageVersions(targetMsg);
    const currentVersionIndex = getActiveEditVersionIndex(targetMsg, existingVersions);
    const currentVersion = existingVersions[currentVersionIndex] || existingVersions[0];
    const effectiveImages = newImages.length > 0
      ? cloneImages(newImages)
      : (cloneImages(currentVersion?.images) ?? cloneImages(targetMsg.images));

    const newBranchId = genBranchId();
    const updatedVersions = [
      ...existingVersions,
      {
        content: trimmedText,
        images: effectiveImages,
        branchId: newBranchId,
      }
    ];

    // Lưu snapshot của branch hiện tại trước khi tách nhánh mới,
    // tránh trường hợp conversation cũ chưa có branchSnapshots nên không navigate được.
    syncSnapshotsForConversation(messages, dataSnapshot || null);
    patchSnapshotsForVersions(msgIndex, updatedVersions);

    const updatedTargetMsg = hydrateMessage({
      ...targetMsg,
      content: trimmedText,
      images: effectiveImages,
      timestamp: new Date(),
      branchId: newBranchId,
      editVersions: updatedVersions,
      editVersionIndex: updatedVersions.length - 1,
    });

    const prefixMessages = hydrateMessages(messages.slice(0, msgIndex));
    const baseMessages = [...prefixMessages, updatedTargetMsg];

    setMessages(baseMessages);
    setShowSuggestions(false);
    setIsLoading(true);

    const requestAbortController = new AbortController();
    activeChatAbortRef.current = requestAbortController;

    try {
      const history = prefixMessages
        .filter(message => message.role !== 'system')
        .map(message => ({ role: message.role, content: message.content }));

      const result = await hrChatService.sendMessage(transportText, history, effectiveImages || [], {
        signal: requestAbortController.signal,
        rawMode: requestOptions?.rawMode === true || isRawModeMessage(transportText)
      });

      // Parse JSON response from AI
      let aiContent = result.response || '';
      try {
        const parsed = JSON.parse(aiContent);
        if (parsed.answer) {
          aiContent = parsed.answer;
        } else if (parsed.content) {
          aiContent = parsed.content;
        }
      } catch {
        // Fallback khi AI trả JSON bị cắt cụt
        const rawAiContent = String(aiContent || '').trim();
        const likelyJsonPayload = rawAiContent.startsWith('{')
          || rawAiContent.startsWith('```json')
          || /"action"\s*:|"answer"\s*:/i.test(rawAiContent);

        if (likelyJsonPayload) {
          const answerMatch = rawAiContent.match(/"answer"\s*:\s*"([\s\S]*)$/i);
          if (answerMatch && answerMatch[1]) {
            aiContent = answerMatch[1]
              .replace(/"\s*\}?\s*$/, '')
              .replace(/\\n/g, '\n')
              .replace(/\\r/g, '')
              .replace(/\\t/g, '\t')
              .replace(/\\"/g, '"')
              .trim();
          }
        }
      }

      aiContent = String(aiContent || '').replace(/\\n/g, '\n');

      const assistantMsg = hydrateMessage({
        role: 'assistant',
        content: aiContent,
        timestamp: new Date(),
        dataSnapshot: result.dataSnapshot,
        meta: result.meta,
        branchId: newBranchId,
      });

      const finalMessages = hydrateMessages([...baseMessages, assistantMsg]);
      setMessages(finalMessages);
      if (result.dataSnapshot) setDataSnapshot(result.dataSnapshot);
      syncSnapshotsForConversation(finalMessages, result.dataSnapshot || dataSnapshot || null);
    } catch (err) {
      if (isCancelledRequest(err)) {
        const stoppedAssistantMsg = hydrateMessage({
          role: 'assistant',
          content: 'Đã dừng phản hồi AI theo yêu cầu.',
          timestamp: new Date(),
          meta: { stopped: true },
          branchId: newBranchId,
        });

        const finalMessages = hydrateMessages([...baseMessages, stoppedAssistantMsg]);
        setMessages(finalMessages);
        syncSnapshotsForConversation(finalMessages, dataSnapshot || null);
      } else {
        const errMsg = err.response?.data?.error || err.message || 'Có lỗi xảy ra';
        const errorAssistantMsg = hydrateMessage({
          role: 'assistant',
          content: errMsg,
          timestamp: new Date(),
          isError: true,
          branchId: newBranchId,
        });

        const finalMessages = hydrateMessages([...baseMessages, errorAssistantMsg]);
        setMessages(finalMessages);
        syncSnapshotsForConversation(finalMessages, dataSnapshot || null);
      }
    } finally {
      if (activeChatAbortRef.current === requestAbortController) {
        activeChatAbortRef.current = null;
      }
      setIsLoading(false);
    }
  }, [messages, isLoading, dataSnapshot, patchSnapshotsForVersions, syncSnapshotsForConversation, isCancelledRequest]);

  const navigateBranch = useCallback((msgIndex, direction) => {
    if (isLoading) return;

    const targetMsg = messages[msgIndex];
    if (!targetMsg || targetMsg.role !== 'user') return;

    const versions = getUserMessageVersions(targetMsg);
    if (versions.length < 2) return;

    const currentIndex = getActiveEditVersionIndex(targetMsg, versions);
    const nextIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex < 0 || nextIndex >= versions.length) return;

    const nextVersion = versions[nextIndex];
    const snapshotEntry = branchSnapshots.get(nextVersion.branchId);
    if (!snapshotEntry?.messages?.length) return;

    const restoredMessages = hydrateMessages(snapshotEntry.messages);
    if (!restoredMessages[msgIndex] || restoredMessages[msgIndex].role !== 'user') return;

    restoredMessages[msgIndex] = hydrateMessage({
      ...restoredMessages[msgIndex],
      content: nextVersion.content,
      images: cloneImages(nextVersion.images),
      branchId: nextVersion.branchId,
      editVersions: versions,
      editVersionIndex: nextIndex,
    });

    setMessages(restoredMessages);
    setDataSnapshot(snapshotEntry.dataSnapshot || null);
    setShowSuggestions(false);
  }, [messages, isLoading, branchSnapshots]);

  // ── New chat ──
  const startNewChat = useCallback(() => {
    const newId = genConvId();
    const welcome = buildWelcome();
    const emptySnapshots = new Map();

    setMessages([welcome]);
    setDataSnapshot(null);
    setBranchSnapshots(emptySnapshots);
    setActiveConvId(newId);
    setShowSuggestions(true);
    localStorage.setItem(AI_LS_ACTIVE_ID, newId);
    saveConvData(newId, [welcome], null, emptySnapshots);
  }, [buildWelcome]);

  // ── Load conversation ──
  const loadConversation = useCallback((meta) => {
    if (meta.id === activeConvId) return;
    const data = loadConvData(meta.id);
    if (!data?.messages?.length) return;

    setMessages(data.messages);
    setDataSnapshot(data.dataSnapshot || null);
    setBranchSnapshots(data.branchSnapshots || new Map());
    setActiveConvId(meta.id);
    localStorage.setItem(AI_LS_ACTIVE_ID, meta.id);
    setShowSuggestions(false);
  }, [activeConvId]);

  // ── Delete conversation ──
  const deleteConversation = useCallback((convId) => {
    try { localStorage.removeItem(aiLsConvKey(convId)); } catch {}
    setConvList(prev => {
      const updated = prev.filter(c => c.id !== convId);
      try { localStorage.setItem(AI_LS_CONV_LIST, JSON.stringify(updated)); } catch {}
      return updated;
    });
    if (convId === activeConvId) startNewChat();
  }, [activeConvId, startNewChat]);

  // ── Clear all ──
  const clearAllHistory = useCallback(() => {
    convList.forEach(c => { try { localStorage.removeItem(aiLsConvKey(c.id)); } catch {} });
    try { localStorage.removeItem(AI_LS_CONV_LIST); } catch {}
    setConvList([]);
    startNewChat();
  }, [convList, startNewChat]);

  return (
    <AIChatContext.Provider value={{
      messages,
      isLoading,
      convList,
      activeConvId,
      dataSnapshot,
      showSuggestions,
      setShowSuggestions,
      templateSections,
      smartSuggestions,
      sendMessage,
      stopGeneration,
      sendTemplateMessage,
      editMessage,
      navigateBranch,
      startNewChat,
      loadConversation,
      deleteConversation,
      clearAllHistory,
      role,
      roleConfig,
    }}>
      {children}
    </AIChatContext.Provider>
  );
};

export const useAIChat = () => {
  const ctx = useContext(AIChatContext);
  if (!ctx) throw new Error('useAIChat must be used within AIChatProvider');
  return ctx;
};

export default AIChatContext;
